import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  CsAlertBoxRef,
  CsAlertBoxService,
  CsAlertResponse
} from '@cbuild/alert-box';
import { SelectOptions, SnackBarService } from '@cbuild/controls';
import { ApplicationService, AuthenticationService } from '@cbuild/core';
import { CS_DIALOG_DATA, DialogRef } from '@cbuild/dialog';
import {
  CALL_SOURCE_ENUM_LIST,
  CallSourceEnum,
  OrganizationConfigurationEnum
} from '@cbuild/enums';
import {
  Feature,
  FormStateControl,
  LeadFeaturePermissionMask,
  PermissionBaseMixin
} from '@cbuild/shared';
import { Observable, of, switchMap, take } from 'rxjs';
import {
  FeedbackModel,
  SubFeedbackModel
} from '../../../../sales/leads/models/feedback.model';
import { CallCenterService } from '../call-center.service';
import { CallQueueStatus } from '../constants/call-queue-status';
import {
  CallResponseModel,
  SelectOptionsWithStages
} from '../models/call-response-model';
const _BASE = PermissionBaseMixin(FormStateControl);
@Component({
  selector: 'cs-call-drop-popup',
  standalone: false,
  templateUrl: './call-drop-popup.component.html',
  styleUrls: ['./call-drop-popup.component.scss']
})
export class CallDropPopupComponent extends _BASE implements OnInit, OnDestroy {
  public activeFunction: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 = 0;
  public $stagesOptions: Observable<SelectOptionsWithStages>;
  public subSourceOptions$: Observable<SelectOptions>;
  public users: SelectOptions;
  public properties: SelectOptions;
  public canCallConference = false;
  public $feedbackOptions: Observable<SelectOptions> | undefined;
  public areFeedbacksLoading = false;
  public feature = Feature;
  public clickToCallRef: CsAlertBoxRef;
  public leadFeaturePermissionMask = LeadFeaturePermissionMask;
  public canEditName: boolean = false;
  public canEditMobileNo: boolean = false;
  public canEditAlternateMobileNo: boolean = false;
  public canEditEmail: boolean = false;
  public canEditAlternateEmail: boolean = false;
  public canEditSubSource: boolean = false;
  public callConferenceError: string;
  public orgConfigEnum = OrganizationConfigurationEnum;
  public feedbacks: Array<FeedbackModel> = [];
  public subFeedbacks: Array<SubFeedbackModel> = [];
  public isFeedbackUpdateMandatory: boolean;
  public hasMandatoryFollowUp: boolean;
  public teamUsersCount: number = 0;
  public data: Array<{ label: string; value: number }>;

  constructor(
    private service: CallCenterService,
    private sanitizer: DomSanitizer,
    private applicationService: ApplicationService,
    private ref: DialogRef<CallDropPopupComponent, {}>,
    @Inject(CS_DIALOG_DATA)
    public lead: CallResponseModel & {
      isCallConferenceEnabled: boolean;
      from?: string | null;
    },
    private fb: FormBuilder,
    private alertService: CsAlertBoxService,
    private snackbar: SnackBarService,
    authenticationService: AuthenticationService
  ) {
    super(authenticationService);
  }

  public activateFunction(index: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7): void {
    this.activeFunction = index;
  }

  public ngOnInit(): void {
    this.service
      .getPriceConfiguration(this.orgConfigEnum.FeedbackUpdate)
      .pipe(take(1))
      .subscribe(res => {
        if (res.boolean) {
          if (this.lead.winProbability === 100) {
            this.isFeedbackUpdateMandatory = true;
            this.service
              .getLeadFeedbacks(this.lead.pipelineId, this.lead.stageId)
              .pipe(take(1))
              .subscribe(res => {
                this.feedbacks = res;
                this.onFeedbackChange();
              });
          } else {
            this.isFeedbackUpdateMandatory = true;
            this.service
              .getLeadFeedbacks(this.lead.pipelineId, null)
              .pipe(take(1))
              .subscribe(res => {
                this.feedbacks = res;
                this.onFeedbackChange();
              });
          }
        } else {
          this.isFeedbackUpdateMandatory = false;
          this.service
            .getLeadFeedbacks(this.lead.pipelineId, this.lead.stageId)
            .pipe(take(1))
            .subscribe(res => {
              this.feedbacks = res;
              if (this.lead.winProbability !== 100) {
                this.onFeedbackChange();
              }
            });
        }
      });
    this.$stagesOptions = this.service.getLeadStagesWithMandatory(
      this.lead.pipelineId
    );
    this.subSourceOptions$ = this.service.getSubSources(this.lead.sourceId);
    this.getProperties();
    this.getPipeLines();
    this.service
      .getUsers()
      .pipe(take(1))
      .subscribe(resp => {
        this.users = resp.filter(
          f => f.value !== this.authenticationService.getUser()!.uid
        );
      });
    this.infoForm = this.fb.group({
      leadId: [this.lead.leadId],
      propertyId: [],
      name: [this.lead.name],
      mobileNo: [this.lead.mobile],
      alternateMobile: [this.lead.alternateMobile],
      email: [this.lead.email],
      alternateEmail: [this.lead.alternateEmail],
      subSource: [this.lead.subSource],
      stageId: [this.lead.stageId],
      stageTemplateId: [],
      currentStageId: [this.lead.stageId],
      subSourceId: [this.lead.subSourceId],
      feedbackId: [this.lead.feedbackId],
      subFeedbackId: [this.lead.subFeedbackId],
      remarks: [],
      leadActionLogId: [this.lead.leadActionLogId],
      pipelineId: [this.lead.pipelineId],
      taskPipelineId: [this.lead.pipelineId],
      transferTo: [],
      isEmail: [],
      isSms: [],
      isWhatsapp: []
    });
    this.stageTemplateGet();
  }

  public onFeedbackChange(): void {
    const feedbackId = this.getControl('feedbackId').value;
    const currentStageId = this.getControl('stageId').value;
    let stageId =
      this.lead.stageId ??
      this.feedbacks.find(x => x.value === feedbackId)?.stageId;
    if (this.lead.stageId !== currentStageId) {
      stageId = currentStageId;
    }
    if (this.lead.feedbackId !== feedbackId) {
      stageId = this.feedbacks.find(x => x.value === feedbackId)?.stageId ?? 0;
    }

    if (
      !this.feedbacks.find(x => x.stageId === stageId && x.value === feedbackId)
    ) {
      this.getControl('feedbackId').setValue(null);
    }

    // Subscribe to the filtered observable
    this.$stagesOptions.subscribe(options => {
      const filteredOption = options.find(option => option.value === stageId);
      if (filteredOption) {
        this.hasMandatoryFollowUp = filteredOption.hasMandatoryFollowUp;
      }
    });
    this.getControl('stageId').setValue(stageId);
    this.service
      .getLeadSubFeedbacks(feedbackId)
      .pipe(take(1))
      .subscribe(res => {
        this.subFeedbacks = res;
      });
  }

  public stageTemplateGet(): void {
    const { stageId } = this.infoForm.value;
    this.service
      .getStageTemplate(stageId)
      .pipe(take(1))
      .subscribe(res => {
        this.infoForm.get('stageTemplateId')?.setValue(res.templateId);
      });
  }

  public onStageChange(): void {
    this.getControl('feedbackId').setValue(null);
    this.feedbacks = [];
    const { stageId } = this.infoForm.value;
    this.areFeedbacksLoading = true;
    this.stageTemplateGet();
    this.service
      .getLeadFeedbacks(this.lead.pipelineId, stageId)
      .pipe(take(1))
      .subscribe(res => {
        this.feedbacks = res;
        this.areFeedbacksLoading = false;
      });
    // Subscribe to the filtered observable
    this.$stagesOptions.subscribe(options => {
      const filteredOption = options.find(option => option.value === stageId);
      if (filteredOption) {
        this.hasMandatoryFollowUp = filteredOption.hasMandatoryFollowUp;
      }
    });
  }

  public onPropertyChange(): void {
    const { propertyId } = this.infoForm.value;
    this.service
      .getPropertyTeamUserCount(propertyId)
      .pipe(take(1))
      .subscribe(res => {
        this.teamUsersCount = res.count;
      });
  }

  public saveCallFeedback(actionType: 'Save' | 'PauseOrResume' | 'Kill'): void {
    this.clickedSubmit = true;
    this.markFormAsTouched();
    if (!this.dataSentOnce && this.infoForm.valid) {
      this.dataSentOnce = true;
      if (this.lead.from === 'leadBoard') {
        this.saveFeedback();
      } else {
        this.saveActionLog(actionType);
      }
    }
  }

  public saveFeedback(): void {
    this.service
      .updateFeedback(this.infoForm.value)
      .pipe(take(1))
      .subscribe(
        res => {
          this.ref.close(res);
        },
        error => {
          if (error && error.errorCode) {
            this.snackbar.open(error.errorMessage, {
              type: 'error'
            });
          } else {
            this.snackbar.open('Error occurred, please try again', {
              type: 'error'
            });
          }
          this.dataSentOnce = false;
        }
      );
  }

  public saveActionLog(actionType: string): void {
    this.service
      .saveCallActionLog({ leadId: this.lead.leadId })
      .pipe(take(1))
      .subscribe(res => {
        const data = {
          ...this.infoForm.value,
          ...{
            callRecords: this.lead.callRecords,
            activityGroupId: res.activityGroupId
          }
        };
        this.service
          .saveCallFeedback(data)
          .pipe(
            switchMap(_ => {
              if (actionType === 'Save' && this.canTriggerNextQueueItem) {
                return this.service.triggerNextCall();
              } else if (actionType === 'PauseOrResume') {
                return this.pauseOrResumeQueue();
              } else if (actionType === 'Kill') {
                return this.killQueue();
              }
              return of(_);
            }),
            take(1)
          )
          .subscribe(
            _ => {
              this.ref.close({});
              this.applicationService.setNotifyCallPopupClose(this.lead.leadId);
            },
            error => {
              if (error && error.errorCode) {
                this.snackbar.open(error.errorMessage, {
                  type: 'error'
                });
              } else {
                this.snackbar.open('Error occurred, please try again', {
                  type: 'error'
                });
              }
              this.dataSentOnce = false;
            }
          );
      });
  }

  public callConference(leadActionLogId: number): void {
    const userId = this.getControl('transferTo').value;
    if (!userId) {
      this.callConferenceError = 'Please select user';
    } else {
      this.callConferenceError = '';
      this.service
        .callConference({ leadActionLogId, userId })
        .pipe(take(1))
        .subscribe(
          _ => {
            this.canCallConference = false;
          },
          err => {
            this.callConferenceError = err.errorMessage;
          }
        );
    }
  }

  public pauseOrResumeQueue(): Observable<CallResponseModel | undefined> {
    if (this.canPauseQueue || this.canResumeQueue) {
      return this.service.pauseOrResumeCall(this.lead!.queueId!);
    }
    return of(undefined);
  }

  public killQueue(): Observable<CallResponseModel | undefined> {
    if (this.canTerminateQueue) {
      return this.service.KillQueue(this.lead.queueId);
    }
    return of(undefined);
  }

  public get canTriggerNextQueueItem(): boolean {
    return !!this.lead && !!this.lead.queueId;
  }

  public get canTerminateQueue(): boolean {
    return (
      !!this.lead &&
      !!this.lead.queueId &&
      this.lead.queueStatus !== CallQueueStatus.COMPLETED
    );
  }

  public get canPauseQueue(): boolean {
    return (
      !!this.lead?.queueStatus &&
      this.lead.queueStatus === CallQueueStatus.IN_PROGRESS
    );
  }

  public get canResumeQueue(): boolean {
    return (
      !!this.lead?.queueStatus &&
      this.lead.queueStatus === CallQueueStatus.PAUSED
    );
  }

  public get textPauseResume(): string {
    if (this.canPauseQueue) {
      return 'Pause';
    } else if (this.canResumeQueue) {
      return 'Resume';
    }
    return '';
  }

  public clickToCall(
    leadItem: CallResponseModel,
    isAlternateMobileCall: boolean
  ): void {
    const ref: CsAlertBoxRef = this.alertService.open({
      title: 'Make a Call',
      displayMessage: 'Are you sure, you want to make a call ?',
      width: '500px',
      alertType: 'confirm'
    });
    this.clickToCallRef = ref;
    const sub = ref.afterClosed.subscribe((resp: CsAlertResponse) => {
      if (resp.primaryBtnClicked) {
        this.callRequest(leadItem, isAlternateMobileCall);
      }
      sub.unsubscribe();
    });
  }

  public callRequest(
    leadItem: CallResponseModel,
    isAlternateMobileCall: boolean
  ): void {
    this.service
      .clickToCall(leadItem.leadId, isAlternateMobileCall)
      .pipe(take(1))
      .subscribe(
        res => {
          if (res) {
            this.showSuccessMessage();
          } else {
            this.showErrorMessage();
          }
        },
        err => {
          this.snackbar.open(err.errorMessage, { type: 'error' });
        }
      );
  }

  public showSuccessMessage(): void {
    this.snackbar.open('Call placed successfully', {
      type: 'success'
    });
  }

  public showErrorMessage(): void {
    this.snackbar.open('Call placed successfully', {
      type: 'success'
    });
  }

  public getProperties(): void {
    this.service
      .getProperties(this.lead?.companyId)
      .pipe(take(1))
      .subscribe(res => {
        this.properties = res;
      });
  }

  private getPipeLines(): void {
    this.service
      .getAllPipelines()
      .pipe(take(1))
      .subscribe(result => {
        this.data = result;
      });
  }

  public ngOnDestroy(): void {
    this.clickToCallRef?.close();
  }

  public getCallSource(status: CallSourceEnum): string {
    return CALL_SOURCE_ENUM_LIST[status];
  }

  getTrustURL(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  onProgress(audioPlayer: HTMLAudioElement) {
    if (audioPlayer.buffered.length > 0) {
      const sourceElement = audioPlayer.querySelector('source');
      const bufferedEnd = audioPlayer.buffered.end(0);
      const duration = audioPlayer.duration;
      console.log(
        `Buffered ${bufferedEnd} out of ${duration} seconds. ` +
          sourceElement?.src
      );
    }
  }
}
